import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';

import { flagsContext } from '@/contexts/flagsContext';
import { parseJson } from '@/utils/string';

import { useEnvironment } from './useEnvironment';

// Splits a ?flags= query string into key→value pairs, handling JSON object/array
// values whose commas and colons would otherwise be misinterpreted as delimiters.
const parseFlagOverrides = (raw: string): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  let i = 0;
  while (i < raw.length) {
    let depth = 0;
    let j = i;
    while (j < raw.length && !(raw[j] === ',' && depth === 0)) {
      if (raw[j] === '{' || raw[j] === '[') {
        depth++;
      } else if (raw[j] === '}' || raw[j] === ']') {
        depth--;
      }
      j++;
    }
    const entry = raw.slice(i, j);
    const sep = entry.indexOf(':');
    if (sep === -1) {
      result[entry] = true;
    } else {
      const key = entry.slice(0, sep);
      const value = entry.slice(sep + 1);
      const parsed = parseJson(value);
      result[key] = parsed === undefined ? value : parsed;
    }
    i = j + 1;
  }
  return result;
};

export const useFeatureFlags = () => {
  const conductedFlags = useFlags(flagsContext);
  const flagsOverrides =
    typeof window === 'undefined'
      ? null
      : new URLSearchParams(window.location.search).get('flags');

  if (!flagsOverrides) {
    return conductedFlags;
  }

  const flags = parseFlagOverrides(flagsOverrides);

  return {
    ...conductedFlags,
    ...flags,
  };
};

export const useLaunchDarkly = () => {
  const { isDemo, isDev, isTest } = useEnvironment();
  const [ldClientReady, setLDClientReady] = useState(isDemo || isDev || isTest);
  const ldClient = useLDClient(flagsContext);

  useEffect(() => {
    void ldClient?.waitUntilReady().then(() => setLDClientReady(true));
  }, [ldClient]);

  return { isReady: ldClientReady };
};
